import React, { useState, useEffect } from 'react';
import './ExpressCheckoutModal.scss';
import { Imp } from '../../../models/payment/iamport/IMP';
import {
  IamportPaymentRequestModel,
  IamportPayMethod,
  ImpCallbackResponseModel,
} from '../../../models/payment/iamport/ImpModels';
import { api } from '../../../config/api';
import { util } from '../../../config/util';
import { Cookies } from '../../../constants/Cookies';
import GlobalEnvironments from '../../../modules/globalEnvironment/GlobalEnvironment';

// Kakao SDK 타입 선언
declare global {
  interface Window {
    Kakao: {
      init: (apiKey: string) => void;
      isInitialized: () => boolean;
      Auth: {
        authorize: (options: { redirectUri: string }) => void;
      };
    };
  }
}

interface SelectedPlan {
  id: string;
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
}

interface UserData {
  name?: string;
  phone?: string;
  email?: string;
  provider?: string;
  providerId?: string;
  isLoggedIn?: boolean; // 로그인 상태 플래그
  authKey?: string;
  imp_uid?: string;
  needsAdditionalInfo?: boolean; // 신규 사용자 추가 정보 필요 플래그
  subscription?: {
    subscription_id: string;
    trial_end_date: string;
  };
  paymentInfo?: {
    imp_uid?: string;
    merchant_uid?: string;
    amount?: number;
  };
  cardInfo?: BillingCardInfo;
}

// Express Checkout 진행 상태 저장용 인터페이스
interface ExpressCheckoutPending {
  userInfo: UserData;
  selectedPlans: SelectedPlan[];
  isSubscription: boolean;
  timestamp: number;
  step: 'auth' | 'payment' | 'success';
  paymentData?: BillingCardInfo;
}

// SubscriptionCardModal 기반 결제정보 구조
interface BillingCardInfo {
  card_number: string; // "5389-2000-7401-8546"
  expiry: string; // "2028-02"
  birth?: string; // 개인카드: "981229"
  business_num?: string; // 사업자카드: "1234567890"
  pwd_2digit: string; // "10"
  card_type: "personal" | "business";
}

// 간소화된 회원가입 정보
interface MinimalSignupInfo {
  username: string; // 이메일 (아이디)
  password1: string; // 비밀번호
  password2: string; // 비밀번호 확인
  last_name: string; // 이름
  phone: string; // 전화번호
  addr: string; // 주소
  privacy_agree: boolean; // 개인정보처리방침 (필수)
  terms_agree: boolean; // 이용약관 (필수)
}

interface ExpressCheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPlans: SelectedPlan[];
  totalAmount: number;
  isMonthly: boolean;
  isSubscription: boolean;
  isLoggedIn?: boolean;
  onSuccess: (userData: UserData) => void;
  onSocialLogin: (provider: 'kakao' | 'naver' | 'google') => Promise<UserData>;
  onPhoneAuth: (phoneNumber: string) => Promise<{ success: boolean; userData: UserData }>;
  onPaymentComplete: (billingKey: string, subscriptionId: number) => void;
}

const ExpressCheckoutModal: React.FC<ExpressCheckoutModalProps> = ({
  isOpen,
  onClose,
  selectedPlans,
  totalAmount,
  isMonthly,
  isSubscription,
  isLoggedIn = false,
  onSuccess,
  onSocialLogin,
  onPhoneAuth,
  onPaymentComplete,
}) => {
  // 새로운 플로우: payment → auth → phone → success (사용자 요청사항)
  const [currentStep, setCurrentStep] = useState<'payment' | 'auth' | 'phone' | 'success'>('payment');
  const [authMethod, setAuthMethod] = useState<'social' | 'phone' | 'manual' | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // SMS 인증 상태
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [smsTimer, setSmsTimer] = useState(180); // 3분 타이머

  // 결제정보 상태
  const [cardInfo, setCardInfo] = useState<BillingCardInfo>({
    card_number: '',
    expiry: '',
    birth: '',
    business_num: '',
    pwd_2digit: '',
    card_type: 'personal'
  });

  // 간소화된 회원가입 정보
  const [signupInfo, setSignupInfo] = useState<MinimalSignupInfo>({
    username: '',
    password1: '',
    password2: '',
    last_name: '',
    phone: '',
    addr: '',
    privacy_agree: false,
    terms_agree: false
  });

  const [savedUserData, setSavedUserData] = useState<UserData | null>(null);
  const [isAutoReopen, setIsAutoReopen] = useState(false);
  const [isRestoredLoggedIn, setIsRestoredLoggedIn] = useState(false); // 자동 복원 시 로그인 상태

  // LocalStorage 관리 함수들
  const savePendingCheckout = (data: ExpressCheckoutPending) => {
    try {
      localStorage.setItem('express_checkout_pending', JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save pending checkout:', error);
    }
  };

  const getPendingCheckout = (): ExpressCheckoutPending | null => {
    try {
      const data = localStorage.getItem('express_checkout_pending');
      if (!data) return null;
      
      const parsed = JSON.parse(data);
      
      // 타임스탬프 검증 (5분 이내)
      if (Date.now() - parsed.timestamp < 300000) {
        return parsed;
      } else {
        // 만료된 데이터 제거
        localStorage.removeItem('express_checkout_pending');
        return null;
      }
    } catch (error) {
      console.error('Failed to get pending checkout:', error);
      localStorage.removeItem('express_checkout_pending');
      return null;
    }
  };

  const clearPendingCheckout = () => {
    try {
      localStorage.removeItem('express_checkout_pending');
    } catch (error) {
      console.error('Failed to clear pending checkout:', error);
    }
  };

  // Kakao SDK 초기화
  useEffect(() => {
    if (isOpen && !window.Kakao?.isInitialized()) {
      window.Kakao?.init("4e6c55bc6919807933f9f9c2620e7e9d");
    }
  }, [isOpen]);

  // 자동 복원 로직 (모달이 열릴 때)
  useEffect(() => {
    if (isOpen) {
      const pendingData = getPendingCheckout();
      if (pendingData) {
        console.log('ExpressCheckoutModal 자동 복원:', pendingData);
        
        // 상태 복원
        setSavedUserData(pendingData.userInfo);
        setIsAutoReopen(true);
        
        // 로그인 상태 확인 (userInfo에서 isLoggedIn 확인)
        if (pendingData.userInfo?.isLoggedIn) {
          setIsRestoredLoggedIn(true); // 로그인된 상태로 설정
          
          // 로그인된 상태에서의 플로우
          if (!pendingData.isSubscription) {
            // 일반형의 경우: 바로 결제 단계로 이동하고 결제 진행
            setCurrentStep('success');
            setTimeout(() => {
              handleLoggedInRegularPayment();
            }, 1000);
          } else {
            // 구독형의 경우: payment 단계에서 대기 (카드 정보는 이미 입력됨)
            setCurrentStep('payment');
            // 결제 정보도 복원
            if (pendingData.paymentData) {
              setCardInfo(pendingData.paymentData);
            }
          }
        } else {
          // 로그인되지 않은 상태에서의 복원
          setCurrentStep(pendingData.step);
          if (pendingData.paymentData) {
            setCardInfo(pendingData.paymentData);
          }
        }
        
        // 로컬스토리지 정리
        clearPendingCheckout();
      }
    }
  }, [isOpen]);

  // 플로우 결정: 로그인 상태에 따른 분기 처리
  useEffect(() => {
    console.log('🔄 [ExpressCheckout] 플로우 결정 useEffect 실행');
    console.log('🔄 [ExpressCheckout] isOpen:', isOpen);
    console.log('🔄 [ExpressCheckout] currentStep:', currentStep);
    console.log('🔄 [ExpressCheckout] isLoggedIn:', isLoggedIn);
    console.log('🔄 [ExpressCheckout] isAutoReopen:', isAutoReopen);
    console.log('🔄 [ExpressCheckout] savedUserData:', savedUserData);
    console.log('🔄 [ExpressCheckout] isSubscription:', isSubscription);
    
    if (!isOpen) {
      console.log('🔄 [ExpressCheckout] 모달 닫혀있음, useEffect 종료');
      return;
    }
    
    // 자동 복원된 경우 플로우 건너뛰기
    if (isAutoReopen && savedUserData) {
      console.log('🔄 [ExpressCheckout] 자동 복원 모드, 플로우 건너뛰기');
      return;
    }
    
    // 이미 로그인된 상태일 경우: 바로 결제 단계로 진행
    if (isLoggedIn && currentStep === 'payment') {
      console.log('🔐 [ExpressCheckout] 로그인된 상태 - 바로 결제 가능');
      // 로그인된 사용자 정보를 savedUserData에 설정 (기본값)
      if (!savedUserData) {
        setSavedUserData({
          name: '로그인된 사용자',
          email: 'user@example.com',
          phone: '010-0000-0000',
          provider: 'existing',
          isLoggedIn: true,
          needsAdditionalInfo: false
        });
      }
      return; // payment 단계 유지 (로그인 상태 표시와 함께)
    }
    
    // 이미 진행 중인 단계가 있고 savedUserData가 설정된 경우 (SNS 로그인 완료 후), 단계 변경 방지
    if (savedUserData && (currentStep === 'phone' || currentStep === 'success')) {
      console.log('🔄 [ExpressCheckout] SNS 로그인 후 단계, 변경하지 않음');
      return;
    }
    
    // 구독형은 항상 payment부터 시작 (카드정보 → SNS 로그인 → SMS 인증)
    if (isSubscription && currentStep === 'payment') {
      console.log('🔄 [ExpressCheckout] 구독형 → payment 단계 유지');
      // 이미 payment 단계이므로 변경하지 않음
    } else if (isSubscription && currentStep === 'auth') {
      console.log('🔄 [ExpressCheckout] 구독형 → auth 단계 유지 (결제정보 완료 후)');
      // 결제정보 완료 후 auth 단계이므로 유지
    } else if (isSubscription && !savedUserData && currentStep !== 'auth' && !isLoggedIn) {
      console.log('🔄 [ExpressCheckout] 구독형 초기화 → payment 단계로 설정');
      setCurrentStep('payment');
    } else if (!isSubscription) {
      // 일반형은 기존 로직 유지 (현재는 사용하지 않음)
      const effectivelyLoggedIn = isLoggedIn || isRestoredLoggedIn;
      console.log('🔄 [ExpressCheckout] 일반형, effectivelyLoggedIn:', effectivelyLoggedIn);
      if (effectivelyLoggedIn && currentStep !== 'success') {
        console.log('🔄 [ExpressCheckout] 로그인됨 → 일반 결제로 진행');
        // handleLoggedInRegularPayment는 별도의 useEffect에서 처리
        setCurrentStep('success'); // 임시로 success 단계로 설정하여 무한 루프 방지
      } else if (!effectivelyLoggedIn && currentStep !== 'auth') {
        console.log('🔄 [ExpressCheckout] 비로그인 → auth 단계로 설정');
        setCurrentStep('auth');
      }
    }
  }, [isSubscription, isOpen, isLoggedIn, isRestoredLoggedIn, isAutoReopen, savedUserData, currentStep]);

  // 로컬스토리지 결제정보 관리
  const savePaymentInfo = (cardInfo: BillingCardInfo) => {
    try {
      const encrypted = btoa(JSON.stringify(cardInfo));
      localStorage.setItem('express_payment_temp', encrypted);
      
      // 10분 후 자동 삭제
      setTimeout(() => {
        localStorage.removeItem('express_payment_temp');
      }, 600000);
    } catch (error) {
      console.error('결제정보 저장 오류:', error);
    }
  };

  const getPaymentInfo = (): BillingCardInfo | null => {
    try {
      const encrypted = localStorage.getItem('express_payment_temp');
      if (!encrypted) return null;
      
      return JSON.parse(atob(encrypted));
    } catch {
      localStorage.removeItem('express_payment_temp');
      return null;
    }
  };

  // 컴포넌트 마운트 시 저장된 결제정보 복원
  useEffect(() => {
    if (isOpen && isSubscription) {
      const savedPayment = getPaymentInfo();
      if (savedPayment) {
        setCardInfo(savedPayment);
      }
    }
  }, [isOpen, isSubscription]);

  if (!isOpen) return null;

  // 로그인된 사용자의 일반 결제 처리 (KG이니시스)
  const handleLoggedInRegularPayment = async () => {
    try {
      setIsLoading(true);
      
      // 자동 복원된 사용자 정보가 있으면 사용, 없으면 기본값 사용
      const userData: UserData = savedUserData || {
        name: '로그인된 사용자',
        email: 'user@example.com',
        phone: '01012345678',
        provider: 'existing'
      };
      
      setSavedUserData(userData);
      
      // KG이니시스 결제 모달 호출
      await startRegularPayment(userData);
      
    } catch (err: any) {
      setError('결제 처리 중 오류가 발생했습니다.');
      setIsLoading(false);
    }
  };

  // KG이니시스 일반 결제 처리
  const startRegularPayment = async (userData: UserData) => {
    try {
      // 결제 요청 데이터 생성
      const requestData: IamportPaymentRequestModel = {
        pg: GlobalEnvironments.getPgProvider(),
        amount: totalAmount,
        buyer_tel: userData.phone || "",
        pay_method: IamportPayMethod.card,
        merchant_uid: `express_${new Date().getTime()}`,
        name: selectedPlans.map(p => p.name).join(', '),
        buyer_name: userData.name || "",
        buyer_addr: "",
        buyer_email: userData.email || "",
        m_redirect_url: `${window.location.origin}/payment/success`,
      };

      // KG이니시스 결제창 호출
      Imp.requestPay(requestData, handleRegularPaymentCallback);
      
    } catch (error) {
      console.error("일반 결제 오류:", error);
      setError("결제 처리 중 오류가 발생했습니다.");
      setIsLoading(false);
    }
  };

  // 일반 결제 콜백 처리
  const handleRegularPaymentCallback = async (res: ImpCallbackResponseModel) => {
    try {
      const hasError = res?.error_code || res?.error_msg;
      const noImpUid = !res?.imp_uid;
      if (hasError || noImpUid) {
        if (res?.error_msg?.includes("취소")) {
          setError("사용자의 요청으로 결제가 취소되었습니다.");
          setIsLoading(false);
          return;
        }
        setError(res?.error_msg || "결제가 정상적으로 완료되지 않았습니다.");
        setIsLoading(false);
        return;
      }

      // 결제 성공 처리
      setCurrentStep('success');
      
      // 성공 데이터 전달
      onSuccess({
        ...savedUserData!,
        paymentInfo: {
          imp_uid: res.imp_uid,
          merchant_uid: res.merchant_uid,
          amount: res.paid_amount,
        }
      });
      
    } catch (error) {
      console.error("결제 콜백 오류:", error);
      setError("결제 처리 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 결제정보 입력 완료 처리 (구독형만) - 새로운 플로우: 카드정보 저장 → 회원가입 → 빌링키 발급
  const handlePaymentInfoComplete = async () => {
    // 입력 검증
    if (!validateCardInfo()) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      console.log('💳 [ExpressCheckout] 카드 정보 저장 시작...');
      
      // 로그인된 사용자인 경우: 바로 빌링키 발급 시도
      if (isLoggedIn && savedUserData?.isLoggedIn) {
        console.log('🔐 [ExpressCheckout] 로그인된 사용자 - 빌링키 발급 시도');
        
        // API 요청 데이터 구성
        const requestData = {
          card_number: cardInfo.card_number.replace(/[^0-9]/g, ''),
          expiry_month: cardInfo.expiry.split('/')[0],
          expiry_year: cardInfo.expiry.split('/')[1],
          birth: cardInfo.card_type === 'personal' ? cardInfo.birth : undefined,
          business_num: cardInfo.card_type === 'business' ? cardInfo.business_num : undefined,
          pwd_2digit: cardInfo.pwd_2digit
        };

        // 빌링키 발급 API 호출
        const billingRes = await api.postBillingKeyOnlyAsync(requestData);

        if (billingRes?.status === 200 && billingRes.data?.code === 1) {
          console.log('🎉 [ExpressCheckout] 로그인된 사용자 빌링키 발급 성공!');
          
          // 카드 정보 저장
          savePaymentInfo(cardInfo);
          
          // 쿠키에서 authKey 가져오기
          const existingAuthKey = util.getCookie(Cookies.authKey);
          
          // 완료 처리 및 이용권 페이지 이동
          await completeExpressCheckoutFlow(existingAuthKey);
          return;
        } else {
          throw new Error(billingRes?.data?.message || '빌링키 발급에 실패했습니다.');
        }
      } else {
        // 비로그인 사용자인 경우: 카드 정보만 localStorage에 저장
        console.log('👤 [ExpressCheckout] 비로그인 사용자 - 카드 정보 저장 후 인증 단계로 이동');
        
        // 카드 정보를 localStorage에 저장 (로그인 후 빌링키 발급을 위해)
        savePaymentInfo(cardInfo);
        
        // 회원가입을 위한 인증 단계로 이동
        setCurrentStep('auth');
      }
      
    } catch (error: any) {
      console.error('❌ [ExpressCheckout] 카드 정보 처리 오류:', error);
      
      // 에러 메시지 설정
      let errorMessage = '카드 정보 처리 중 오류가 발생했습니다.';
      
      if (error.response?.status === 400) {
        errorMessage = '카드 정보가 올바르지 않습니다. 다시 확인해주세요.';
      } else if (error.response?.status === 401) {
        errorMessage = '로그인이 필요합니다. 다음 단계에서 로그인해주세요.';
      } else if (error.response?.status >= 500) {
        errorMessage = '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // 카드 정보 유효성 검사
  const validateCardInfo = (): boolean => {
    const { card_number, expiry, pwd_2digit, birth, business_num, card_type } = cardInfo;
    
    // 15자리(AMEX) + 3하이픈 = 18자, 16자리 + 3하이픈 = 19자
    if (!card_number || card_number.length < 18) {
      setError('카드 번호를 정확히 입력해주세요. (15-16자리)');
      return false;
    }
    
    if (!expiry || expiry.length < 5) {
      setError('만료일을 정확히 입력해주세요.');
      return false;
    }
    
    if (!pwd_2digit || pwd_2digit.length < 2) {
      setError('카드 비밀번호 앞 2자리를 입력해주세요.');
      return false;
    }
    
    if (card_type === 'personal' && (!birth || birth.length < 6)) {
      setError('생년월일을 정확히 입력해주세요.');
      return false;
    }
    
    if (card_type === 'business' && (!business_num || business_num.length < 10)) {
      setError('사업자등록번호를 정확히 입력해주세요.');
      return false;
    }
    
    return true;
  };

  // 소셜 로그인 처리 - LoginPage와 동일한 방식 (새 탭 팝업으로)
  const handleSocialLogin = (provider: 'kakao' | 'naver' | 'google') => {
    try {
      setIsLoading(true);
      setError(null);
      
      let socialUrl = '';
      
      if (provider === 'kakao') {
        // 카카오 로그인
        if (!window.Kakao.isInitialized()) {
          window.Kakao.init("4e6c55bc6919807933f9f9c2620e7e9d");
        }
        socialUrl = `https://kauth.kakao.com/oauth/authorize?client_id=4e6c55bc6919807933f9f9c2620e7e9d&redirect_uri=${encodeURIComponent(GlobalEnvironments.getAppUrl() + "oauth2/kakao")}&response_type=code`;
      } else if (provider === 'naver') {
        // 네이버 로그인
        const clientId = "5KdLca8FOBTswLiRvb6v";
        const redirectUri = GlobalEnvironments.getAppUrl() + "oauth2/naver";
        socialUrl = `https://nid.naver.com/oauth2.0/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}`;
      } else {
        // 구글 로그인
        const clientId = "419125396470-2rt62duahcpf2odq0nggbm8tskui2rjo.apps.googleusercontent.com";
        const redirectUri = GlobalEnvironments.getAppUrl() + "oauth2/google";
        socialUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=email+profile`;
      }
      
      // 새 탭에서 소셜 로그인 처리 (Callback.tsx가 !!window.opener로 감지)
      const popup = window.open(
        socialUrl,
        'socialLogin',
        'width=500,height=600,scrollbars=yes,resizable=yes'
      );
      
      // postMessage 수신 대기
      const handleMessage = (event: MessageEvent) => {
        console.log('🔔 [ExpressCheckout] PostMessage 수신:', event.data);
        console.log('🔔 [ExpressCheckout] 현재 단계:', currentStep);
        console.log('🔔 [ExpressCheckout] 이벤트 origin:', event.origin);
        console.log('🔔 [ExpressCheckout] window origin:', window.location.origin);
        
        if (event.origin !== window.location.origin) {
          console.log('❌ [ExpressCheckout] Origin 불일치로 메시지 무시');
          return;
        }
        
        if (event.data.type === 'SOCIAL_LOGIN_SUCCESS') {
          const { userInfo } = event.data;
          console.log('✅ [ExpressCheckout] 기존 사용자 로그인 성공:', userInfo);
          console.log('🔍 [ExpressCheckout] userInfo.name:', userInfo.name);
          console.log('🔍 [ExpressCheckout] userInfo.last_name:', userInfo.last_name);
          console.log('🔍 [ExpressCheckout] userInfo.nickname:', userInfo.nickname);
          popup?.close();
          
          // 사용자 이름 결정 로직 - fallback 텍스트 제거
          let userName = userInfo.name;
          
          // fallback 텍스트인지 확인하고 제거
          if (userName && (
            userName === 'naver 사용자' || 
            userName === 'kakao 사용자' || 
            userName === 'google 사용자' || 
            userName.includes('사용자')
          )) {
            userName = ''; // fallback 텍스트는 빈값으로 처리
          }
          
          if (!userName) userName = userInfo.last_name;
          if (!userName) userName = userInfo.nickname;
          if (!userName) userName = userInfo.full_name;
          if (!userName) userName = userInfo.display_name;
          if (!userName) userName = ''; // 빈값으로 처리
          
          console.log('👤 [ExpressCheckout] 최종 결정된 사용자 이름:', userName);
          
          // SNS 로그인 성공 후 사용자 정보 저장
          const savedData = {
            ...userInfo,
            name: userName,
            provider: provider,
            providerId: userInfo.providerId || userInfo.id,
            isLoggedIn: true,
            needsAdditionalInfo: false // 기존 사용자
          };
          
          console.log('💾 [ExpressCheckout] 기존 사용자 데이터 저장:', savedData);
          setSavedUserData(savedData);
          
          // 전화번호 인증 단계로 이동
          console.log('➡️ [ExpressCheckout] phone 단계로 이동');
          setCurrentStep('phone');
          setIsLoading(false);
          
        } else if (event.data.type === 'SOCIAL_LOGIN_PARTIAL') {
          // 신규 사용자 - 추가 정보 입력 필요
          const { userInfo } = event.data;
          console.log('🆕 [ExpressCheckout] 신규 사용자 부분 로그인:', userInfo);
          console.log('🔍 [ExpressCheckout] 신규사용자 userInfo.name:', userInfo.name);
          console.log('🔍 [ExpressCheckout] 신규사용자 userInfo.last_name:', userInfo.last_name);
          console.log('🔍 [ExpressCheckout] 신규사용자 userInfo.nickname:', userInfo.nickname);
          popup?.close();
          
          // 사용자 이름 결정 로직 - fallback 텍스트 제거
          let userName = userInfo.name;
          
          // fallback 텍스트인지 확인하고 제거
          if (userName && (
            userName === 'naver 사용자' || 
            userName === 'kakao 사용자' || 
            userName === 'google 사용자' || 
            userName.includes('사용자')
          )) {
            userName = ''; // fallback 텍스트는 빈값으로 처리
          }
          
          if (!userName) userName = userInfo.last_name;
          if (!userName) userName = userInfo.nickname;
          if (!userName) userName = userInfo.full_name;
          if (!userName) userName = userInfo.display_name;
          if (!userName) userName = ''; // 빈값으로 처리
          
          console.log('👤 [ExpressCheckout] 신규사용자 최종 결정된 사용자 이름:', userName);
          
          // 소셜 로그인 정보 저장
          const savedData = {
            ...userInfo,
            name: userName,
            provider: provider,
            providerId: userInfo.providerId,
            needsAdditionalInfo: true // 신규 사용자
          };
          
          console.log('💾 [ExpressCheckout] 신규 사용자 데이터 저장:', savedData);
          setSavedUserData(savedData);
          
          // 전화번호 인증 단계로 이동
          console.log('➡️ [ExpressCheckout] phone 단계로 이동');
          setCurrentStep('phone');
          setIsLoading(false);
          
        } else if (event.data.type === 'SOCIAL_LOGIN_ERROR') {
          console.log('❌ [ExpressCheckout] 소셜 로그인 오류:', event.data.error);
          popup?.close();
          setError('로그인 중 오류가 발생했습니다.');
          setIsLoading(false);
        } else {
          console.log('❓ [ExpressCheckout] 알 수 없는 메시지 타입:', event.data.type);
        }
      };
      
      window.addEventListener('message', handleMessage);
      
      // 팝업 닫힘 감지
      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', handleMessage);
          setIsLoading(false);
        }
      }, 1000);
      
    } catch (err: any) {
      setError(`${provider} 로그인에 실패했습니다. 다시 시도해주세요.`);
      setIsLoading(false);
    }
  };


  // 수동 회원가입 처리
  const handleManualSignup = async () => {
    if (!validateSignupInfo()) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      // 회원가입 API 호출 (임시)
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const userData: UserData = {
        name: signupInfo.last_name,
        email: signupInfo.username,
        phone: signupInfo.phone,
        provider: 'manual'
      };
      
      setSavedUserData(userData);
      proceedToFinalPayment(userData);
      
    } catch (err: any) {
      setError('회원가입 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 회원가입 정보 유효성 검사
  const validateSignupInfo = (): boolean => {
    const { username, password1, password2, last_name, phone, privacy_agree, terms_agree } = signupInfo;
    
    if (!username || !username.includes('@')) {
      setError('올바른 이메일 주소를 입력해주세요.');
      return false;
    }
    
    if (!password1 || password1.length < 8) {
      setError('비밀번호는 8자리 이상이어야 합니다.');
      return false;
    }
    
    if (password1 !== password2) {
      setError('비밀번호가 일치하지 않습니다.');
      return false;
    }
    
    if (!last_name) {
      setError('이름을 입력해주세요.');
      return false;
    }
    
    if (!phone || phone.length < 11) {
      setError('올바른 전화번호를 입력해주세요.');
      return false;
    }
    
    if (!privacy_agree || !terms_agree) {
      setError('필수 약관에 동의해주세요.');
      return false;
    }
    
    return true;
  };

  // 최종 결제 진행
  const proceedToFinalPayment = async (userData: UserData) => {
    try {
      setIsLoading(true);
      
      if (isSubscription) {
        // 구독형: 빌링키 등록
        const savedPayment = getPaymentInfo();
        if (savedPayment) {
          // 빌링키 등록 API 호출
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          const mockBillingKey = 'billing_' + Date.now();
          const mockSubscriptionId = Math.floor(Math.random() * 10000);
          
          onPaymentComplete(mockBillingKey, mockSubscriptionId);
        }
      } else {
        // 일반 결제: KG이니시스 결제 모달
        // SubscriptionPaymentContainer.tsx 의 startRegularPayment 호출
        await new Promise(resolve => setTimeout(resolve, 1500));
      }
      
      // 성공 페이지로 이동
      setCurrentStep('success');
      
      // 성공 데이터 전달
      onSuccess({
        ...userData,
        subscription: isSubscription ? {
          subscription_id: Math.floor(Math.random() * 10000).toString(),
          trial_end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        } : undefined
      });
      
    } catch (err: any) {
      setError('결제 처리 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 결제정보 입력 단계 (구독형만)
  const renderPaymentStep = () => (
    <div className="express-modal-content">
      <div className="modal-header">
        {isAutoReopen && (
          <div className="auto-continue-notice">
            ✅ 로그인이 완료되었습니다. 결제를 계속 진행하세요.
          </div>
        )}
        <h2>💳 구독 결제정보 입력</h2>
        <p>정기결제를 위한 카드 정보를 먼저 입력해주세요</p>
        <button className="close-button" onClick={onClose}>×</button>
      </div>

      {/* 진행률 표시 */}
      <div className="progress-indicator">
        <div className="step completed">✅ 플랜 선택</div>
        <div className="step active">💳 결제정보 입력</div>
        <div className={`step ${savedUserData ? 'completed' : ''}`}>
          {savedUserData ? '👤 로그인 완료' : '👤 간편가입'}
        </div>
        <div className="step">🎉 완료</div>
      </div>

      {/* 로그인 완료 사용자 정보 표시 */}
      {savedUserData && (
        <div className="login-complete-info">
          <div className="user-welcome">
            <h3>
              {savedUserData.provider === 'kakao' && '💬'} 
              {savedUserData.provider === 'naver' && '🟢'} 
              {savedUserData.provider === 'google' && '🔵'}
              {savedUserData.name ? ` ${savedUserData.name}님, ` : ' '}환영합니다!
            </h3>
            <p>✅ 로그인이 완료되었습니다. 이제 결제정보를 입력해주세요.</p>
            <div className="user-info-mini">
              <span>📧 {savedUserData.email}</span>
              {savedUserData.phone && <span>📞 {savedUserData.phone}</span>}
            </div>
          </div>
        </div>
      )}

      <div className="plan-summary">
        <h3>선택한 플랜</h3>
        {selectedPlans.map((plan) => (
          <div key={plan.id} className="selected-plan">
            <span>{plan.name}</span>
            <span>{(isMonthly ? plan.monthlyPrice : plan.yearlyPrice).toLocaleString()}원/{isMonthly ? '월' : '년'}</span>
          </div>
        ))}
        <div className="total-amount">
          총 결제 금액: <strong>{totalAmount.toLocaleString()}원</strong>
        </div>
      </div>

      <div className="express-payment-info">
        {/* 카드 타입 선택 */}
        <div className="card-type-selection">
          <h4>카드 타입 선택</h4>
          <div className="card-type-buttons">
            <button
              type="button"
              className={`card-type-btn ${cardInfo.card_type === 'personal' ? 'active' : ''}`}
              onClick={() => setCardInfo({...cardInfo, card_type: 'personal'})}
            >
              👤 개인카드
            </button>
            <button
              type="button"
              className={`card-type-btn ${cardInfo.card_type === 'business' ? 'active' : ''}`}
              onClick={() => setCardInfo({...cardInfo, card_type: 'business'})}
            >
              🏢 사업자카드
            </button>
          </div>
        </div>

        {/* 카드 정보 입력 */}
        <div className="payment-form">
          <div className="form-group">
            <label>카드 번호</label>
            <input
              type="text"
              placeholder="0000-0000-0000-0000"
              value={cardInfo.card_number}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '').replace(/(.{4})/g, '$1-').substring(0, 19);
                setCardInfo({...cardInfo, card_number: value});
              }}
              maxLength={19}
            />
            <span className="form-hint" style={{ fontSize: '12px', color: '#666' }}>일반 카드: 16자리, AMEX: 15자리</span>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>만료일</label>
              <input 
                type="text" 
                placeholder="MM/YY"
                value={cardInfo.expiry}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '').replace(/(.{2})/, '$1/').substring(0, 5);
                  setCardInfo({...cardInfo, expiry: value});
                }}
                maxLength={5}
              />
            </div>
            <div className="form-group">
              <label>비밀번호 앞 2자리</label>
              <input 
                type="password" 
                placeholder="••"
                value={cardInfo.pwd_2digit}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '').substring(0, 2);
                  setCardInfo({...cardInfo, pwd_2digit: value});
                }}
                maxLength={2}
              />
            </div>
          </div>

          {/* 개인정보 입력 (카드 타입에 따라) */}
          {cardInfo.card_type === 'personal' ? (
            <div className="form-group">
              <label>생년월일 (6자리)</label>
              <input 
                type="text" 
                placeholder="981229"
                value={cardInfo.birth}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '').substring(0, 6);
                  setCardInfo({...cardInfo, birth: value});
                }}
                maxLength={6}
              />
            </div>
          ) : (
            <div className="form-group">
              <label>사업자등록번호 (10자리)</label>
              <input 
                type="text" 
                placeholder="1234567890"
                value={cardInfo.business_num}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '').substring(0, 10);
                  setCardInfo({...cardInfo, business_num: value});
                }}
                maxLength={10}
              />
            </div>
          )}
        </div>

        {/* 안전성 안내 */}
        <div className="security-info">
          <p>🔒 안전한 SSL 암호화로 보호됩니다</p>
          <p>💳 언제든 취소 가능합니다</p>
        </div>

        <button 
          className="payment-btn"
          onClick={handlePaymentInfoComplete}
          disabled={isLoading}
        >
          {isLoading ? '처리 중...' : (savedUserData ? '구독 결제 시작하기' : '다음 단계: 간편 가입하기')}
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="modal-footer">
        {savedUserData ? (
          <p>결제 정보 입력 후 바로 구독 서비스를 시작하실 수 있습니다.</p>
        ) : (
          <p>결제를 위해 회원가입이 필요합니다. 다음 단계에서 간편하게 가입하세요.</p>
        )}
      </div>
    </div>
  );

  // 인증/회원가입 단계
  const renderAuthStep = () => (
      <div className="express-modal-content">
        <div className="modal-header">
          {isAutoReopen && (
            <div className="auto-continue-notice">
              ✅ 로그인이 완료되었습니다. 결제를 계속 진행하세요.
            </div>
          )}
          <h2>👤 간편 가입하기</h2>
          <p>{isSubscription ? '카드 등록이 완료되었습니다. 이제 간편하게 가입하세요' : '간편하게 가입하고 바로 서비스를 이용해보세요'}</p>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

      {/* 진행률 표시 */}
      <div className="progress-indicator">
        <div className="step completed">✅ 플랜 선택</div>
        <div className={`step ${isSubscription ? 'completed' : 'skipped'}`}>
          {isSubscription ? '💳 결제정보 완료' : '💳 건너뛰기'}
        </div>
        <div className="step active">👤 간편가입</div>
        <div className="step">🎉 완료</div>
      </div>

      {isSubscription && (
        <div className="payment-ready-info">
          <p>✅ 결제정보가 안전하게 저장되었습니다</p>
          <p>이제 간편하게 가입하고 바로 서비스를 시작하세요!</p>
        </div>
      )}

      <div className="auth-methods">
        <div className="auth-section">
          <h3>소셜 로그인으로 간편하게 (새 탭)</h3>
          <div className="social-buttons">
            <button 
              className="social-btn kakao"
              onClick={() => handleSocialLogin('kakao')}
              disabled={isLoading}
            >
              <span className="social-icon">💬</span>
              카카오로 시작하기
            </button>
            <button 
              className="social-btn naver"
              onClick={() => handleSocialLogin('naver')}
              disabled={isLoading}
            >
              <span className="social-icon">🟢</span>
              네이버로 시작하기
            </button>
            <button 
              className="social-btn google"
              onClick={() => handleSocialLogin('google')}
              disabled={isLoading}
            >
              <span className="social-icon">🔵</span>
              구글로 시작하기
            </button>
          </div>
        </div>

        <div className="divider">또는</div>

        {/* 직접 회원가입 */}
        <div className="auth-section">
          <h3>직접 회원가입</h3>
          <button 
            className="manual-signup-btn"
            onClick={() => setAuthMethod('manual')}
            disabled={isLoading}
          >
            📝 직접 입력하여 가입하기
          </button>
        </div>
      </div>

      {/* 수동 회원가입 폼 */}
      {authMethod === 'manual' && (
        <div className="manual-signup-form">
          <div className="form-group">
            <label>이메일 (아이디)</label>
            <input
              type="email"
              placeholder="example@email.com"
              value={signupInfo.username}
              onChange={(e) => setSignupInfo({...signupInfo, username: e.target.value})}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>비밀번호</label>
              <input
                type="password"
                placeholder="8자리 이상"
                value={signupInfo.password1}
                onChange={(e) => setSignupInfo({...signupInfo, password1: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>비밀번호 확인</label>
              <input
                type="password"
                placeholder="비밀번호 재입력"
                value={signupInfo.password2}
                onChange={(e) => setSignupInfo({...signupInfo, password2: e.target.value})}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>이름</label>
              <input
                type="text"
                placeholder="홍길동"
                value={signupInfo.last_name}
                onChange={(e) => setSignupInfo({...signupInfo, last_name: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>전화번호</label>
              <input
                type="tel"
                placeholder="01012345678"
                value={signupInfo.phone}
                onChange={(e) => setSignupInfo({...signupInfo, phone: e.target.value.replace(/[^0-9]/g, '')})}
                maxLength={11}
              />
            </div>
          </div>
          <div className="form-group">
            <label>주소</label>
            <input
              type="text"
              placeholder="서울특별시 강남구..."
              value={signupInfo.addr}
              onChange={(e) => setSignupInfo({...signupInfo, addr: e.target.value})}
            />
          </div>
          
          {/* 약관 동의 */}
          <div className="agreement-section">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={signupInfo.privacy_agree}
                onChange={(e) => setSignupInfo({...signupInfo, privacy_agree: e.target.checked})}
              />
              <span>[필수] 개인정보처리방침에 동의</span>
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={signupInfo.terms_agree}
                onChange={(e) => setSignupInfo({...signupInfo, terms_agree: e.target.checked})}
              />
              <span>[필수] 이용약관에 동의</span>
            </label>
          </div>

          <button 
            className="manual-signup-submit-btn"
            onClick={handleManualSignup}
            disabled={isLoading}
          >
            {isLoading ? '가입 중...' : '회원가입 완료'}
          </button>
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="modal-footer">
        <p>가입 시 <a href="/terms">이용약관</a> 및 <a href="/privacy">개인정보처리방침</a>에 동의하게 됩니다.</p>
      </div>
    </div>
  );

  // 성공 단계
  const renderSuccessStep = () => (
    <div className="express-modal-content">
      <div className="modal-header">
        <h2>🎉 환영합니다!</h2>
        <p>Express Checkout이 완료되었습니다! 곧 이용권 페이지로 이동합니다.</p>
      </div>

      {/* 진행률 표시 */}
      <div className="progress-indicator">
        <div className="step completed">✅ 플랜 선택</div>
        <div className="step completed">
          {isSubscription ? '💳 결제정보 완료' : '💳 건너뛰기'}
        </div>
        <div className="step completed">👤 가입 완료</div>
        <div className="step completed">🎉 완료</div>
      </div>

      <div className="success-info">
        <div className="success-icon">✅</div>
        <div className="success-details">
          <h3>{savedUserData?.name ? `${savedUserData.name}님, ` : ''}환영합니다!</h3>
          <div className="completion-info">
            <p>✅ 회원가입 완료</p>
            <p>💳 카드 정보 저장 완료</p>
            <p>🔄 이용권 페이지로 자동 이동 중...</p>
            <div className="redirect-timer">
              <strong>3초 후 이동합니다</strong>
            </div>
          </div>
        </div>
      </div>

      <div className="user-info-summary">
        <h4>가입 정보</h4>
        <div className="info-item">
          <span>이메일:</span>
          <span>{savedUserData?.email || '이메일 정보 없음'}</span>
        </div>
        <div className="info-item">
          <span>이름:</span>
          <span>{savedUserData?.name || ''}</span>
        </div>
        <div className="info-item">
          <span>전화번호:</span>
          <span>{savedUserData?.phone || '전화번호 정보 없음'}</span>
        </div>
        <div className="info-item">
          <span>가입 방법:</span>
          <span>
            {savedUserData?.provider === 'kakao' && '💬 카카오'}
            {savedUserData?.provider === 'naver' && '🟢 네이버'}
            {savedUserData?.provider === 'google' && '🔵 구글'}
            {savedUserData?.provider === 'phone' && '📞 전화번호 인증'}
            {savedUserData?.provider === 'manual' && '📝 직접 가입'}
          </span>
        </div>
      </div>

      <div className="success-actions">
        <button className="primary-btn" onClick={onClose}>
          🏠 대시보드로 이동
        </button>
        <button className="secondary-btn" onClick={onClose}>
          🔍 서비스 둘러보기
        </button>
      </div>

      <div className="next-steps">
        <h4>다음 단계</h4>
        <ul>
          <li>📋 첫 번째 프로젝트 생성하기</li>
          <li>📊 견적 자동화 기능 체험하기</li>
          <li>💬 고객센터에서 사용법 문의하기</li>
        </ul>
      </div>
    </div>
  );

  // 로컬스토리지 정리 함수
  const cleanup = () => {
    localStorage.removeItem('express_payment_temp');
    setCurrentStep(isSubscription ? 'payment' : 'auth');
    setAuthMethod(null);
    setError(null);
    setCardInfo({
      card_number: '',
      expiry: '',
      birth: '',
      business_num: '',
      pwd_2digit: '',
      card_type: 'personal'
    });
    setSignupInfo({
      username: '',
      password1: '',
      password2: '',
      last_name: '',
      phone: '',
      addr: '',
      privacy_agree: false,
      terms_agree: false
    });
    setSavedUserData(null);
  };


  // 간단한 본인인증 후 완전한 플로우 처리
  const handleDirectCertification = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('📱 [ExpressCheckout] 비로그인 사용자 간단 본인인증 시작...');
      
      // 아임포트 본인인증을 팝업으로 직접 처리
      const { IMP } = window as any;
      if (!IMP) {
        setError('결제 모듈을 불러오는데 실패했습니다. 페이지를 새로고침해주세요.');
        setIsLoading(false);
        return;
      }

      // 본인인증 요청
      IMP.certification({
        merchant_uid: `cert_${Date.now()}`, // 상점 거래ID
        name: '', // 이름 빈값으로 고정
        phone: savedUserData?.phone || '',
        min_age: 18,
        popup: true // 팝업으로 열기
      }, async function(rsp: any) {
        setIsLoading(false);
        
        if (rsp.success) {
          console.log('✅ [ExpressCheckout] 본인인증 성공:', rsp);
          
          // 본인인증 성공 후 완전한 플로우 처리
          await completeExpressCheckoutFlow(util.getCookie(Cookies.authKey));
          
        } else {
          console.error('❌ [ExpressCheckout] 본인인증 실패:', rsp);
          setError(rsp.error_msg || '본인인증에 실패했습니다. 다시 시도해주세요.');
        }
      });
      
    } catch (error) {
      console.error('❌ [ExpressCheckout] 본인인증 처리 오류:', error);
      setError('본인인증 처리 중 오류가 발생했습니다. 다시 시도해주세요.');
      setIsLoading(false);
    }
  };

  // 로그인 완료 후 빌링키 발급 처리 (신규 플로우)
  const handlePostBillingKeySignup = async () => {
    try {
      setIsLoading(true);
      console.log('🔐 [ExpressCheckout] 로그인 완료 후 빌링키 발급 처리 시작...');
      console.log('👤 [ExpressCheckout] 저장된 사용자 정보:', savedUserData);

      let finalAuthKey = savedUserData?.authKey;

      // 신규 사용자인 경우 (SOCIAL_LOGIN_PARTIAL에서 needsAdditionalInfo: true)
      if (savedUserData?.needsAdditionalInfo && savedUserData?.providerId) {
        console.log('🆕 [ExpressCheckout] 신규 사용자 - 본인인증 후 회원가입 진행...');
        
        // 먼저 아임포트 본인인증 진행
        const certificationResult = await performCertificationForSignup();
        
        if (!certificationResult.success) {
          setError('본인인증에 실패했습니다. 다시 시도해주세요.');
          setIsLoading(false);
          return;
        }
        
        // api.socialSignUp 호출 (SocialSignUp.tsx와 동일한 방식)
        const signupResponse = await api.socialSignUp(
          savedUserData.provider || '',
          savedUserData.providerId || '',
          certificationResult.imp_uid // 아임포트 본인인증 ID
        );

        console.log('🎯 [ExpressCheckout] 회원가입 응답:', signupResponse);
        
        if (signupResponse.data?.key) {
          finalAuthKey = signupResponse.data.key;
          
          // 쿠키에 토큰 저장
          const now = new Date();
          util.setCookie({
            cookieName: Cookies.authKey,
            cookieValue: finalAuthKey || '',
            cookieExpire: new Date(now.setFullYear(now.getFullYear() + 1)),
            cookieDomain: GlobalEnvironments.getDomainUrl(),
            cookiePath: "/",
          });
          
          console.log('✅ [ExpressCheckout] 신규 사용자 회원가입 및 로그인 완료');
        } else {
          throw new Error('회원가입에 성공했으나 토큰을 받지 못했습니다.');
        }
      } else if (savedUserData?.authKey) {
        // 기존 사용자인 경우 (SOCIAL_LOGIN_SUCCESS에서 이미 토큰 보유)
        console.log('👤 [ExpressCheckout] 기존 사용자 - 바로 빌링키 발급 진행');
        finalAuthKey = savedUserData.authKey;
      }

      // 로그인 완료 후 빌링키 발급 처리
      console.log('💳 [ExpressCheckout] 로그인 완료 - 빌링키 발급 시작...');
      
      // localStorage에서 저장된 카드 정보 가져오기
      const savedCardInfo = getPaymentInfo();
      if (!savedCardInfo) {
        throw new Error('저장된 카드 정보를 찾을 수 없습니다.');
      }

      // 빌링키 발급 API 호출
      const requestData = {
        card_number: savedCardInfo.card_number.replace(/[^0-9]/g, ''),
        expiry_month: savedCardInfo.expiry.split('/')[0],
        expiry_year: savedCardInfo.expiry.split('/')[1],
        birth: savedCardInfo.card_type === 'personal' ? savedCardInfo.birth : undefined,
        business_num: savedCardInfo.card_type === 'business' ? savedCardInfo.business_num : undefined,
        pwd_2digit: savedCardInfo.pwd_2digit
      };

      console.log('🔑 [ExpressCheckout] 빌링키 발급 API 호출 (로그인 후):', requestData);
      const billingRes = await api.postBillingKeyOnlyAsync(requestData);

      if (billingRes?.status === 200 && billingRes.data?.code === 1) {
        console.log('🎉 [ExpressCheckout] 로그인 후 빌링키 발급 성공!');
        
        // 완료 처리 및 이용권 페이지 이동
        await completeExpressCheckoutFlow(finalAuthKey);
      } else {
        throw new Error(billingRes?.data?.message || '빌링키 발급에 실패했습니다.');
      }

    } catch (error: any) {
      console.error('❌ [ExpressCheckout] 로그인 후 빌링키 발급 실패:', error);
      const errorMessage = error?.message || '카드 등록에 실패했습니다. 카드 정보를 확인 후 다시 시도해주세요.';
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  // 본인인증 처리 (빌링키 발급 후 신규 사용자용)
  const performCertificationForSignup = (): Promise<{success: boolean, imp_uid?: string}> => {
    return new Promise((resolve) => {
      const { IMP } = window as any;
      if (!IMP) {
        resolve({ success: false });
        return;
      }

      // 본인인증 요청
      IMP.certification({
        merchant_uid: `cert_billing_${Date.now()}`,
        name: savedUserData?.name || '',
        phone: savedUserData?.phone || '',
        min_age: 18,
        popup: true
      }, function(rsp: any) {
        if (rsp.success) {
          console.log('✅ [ExpressCheckout] 본인인증 성공:', rsp);
          resolve({ success: true, imp_uid: rsp.imp_uid });
        } else {
          console.error('❌ [ExpressCheckout] 본인인증 실패:', rsp);
          resolve({ success: false });
        }
      });
    });
  };

  // Express Checkout 완료 처리
  const completeExpressCheckoutFlow = async (authKey: string | undefined) => {
    console.log('💳 [ExpressCheckout] Express Checkout 완료 처리...');
    
    // localStorage에 플랜 정보와 카드 정보 저장 (SubscriptionPaymentContainer에서 읽을 수 있도록)
    const selectedPlanInfo = {
      plans: selectedPlans,
      totalAmount: totalAmount,
      isMonthly: isMonthly, // 현재 설정값 그대로 유지
      isSubscription: isSubscription,
      timestamp: Date.now(),
      cardInfo: cardInfo,
      source: 'express_checkout',
      authKey: authKey, // 인증키도 함께 저장
      userData: {
        name: savedUserData?.name,
        email: savedUserData?.email,
        phone: savedUserData?.phone,
        provider: savedUserData?.provider
      }
    };
    
    localStorage.setItem('express_selected_plans', JSON.stringify(selectedPlanInfo));
    console.log('💾 [ExpressCheckout] 선택된 플랜과 카드 정보 저장 완료:', selectedPlanInfo);
    
    // 빌링키 정보를 subscription 페이지에서 사용할 수 있도록 별도 저장
    const billingKeyInfo = {
      cardInfo: cardInfo,
      timestamp: Date.now(),
      source: 'express_checkout',
      authKey: authKey
    };
    localStorage.setItem('express_billing_key_info', JSON.stringify(billingKeyInfo));
    console.log('🔐 [ExpressCheckout] 빌링키 정보 저장 완료');
    
    console.log('✅ [ExpressCheckout] Express Checkout 완료! 이용권 페이지로 이동합니다.');
    
    // success 단계로 이동
    setCurrentStep('success');
    
    // Section7and8의 onSuccess 콜백 호출
    onSuccess({
      name: savedUserData?.name || '',
      email: savedUserData?.email,
      phone: savedUserData?.phone,
      provider: savedUserData?.provider,
      providerId: savedUserData?.providerId,
      isLoggedIn: true,
      authKey: authKey,
      cardInfo: cardInfo,
      subscription: {
        subscription_id: 'express_checkout',
        trial_end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      }
    });
    
    setIsLoading(false);
    
    // 이용권 페이지로 이동 (3초 후)
    setTimeout(() => {
      console.log('🚀 [ExpressCheckout] 이용권 페이지로 이동 실행');
      window.location.href = '/subscription';
    }, 3000);
  };

  
  // SMS 인증 단계 (간단한 본인인증 - 비로그인 사용자용)
  const renderPhoneStep = () => (
    <div className="express-modal-content">
      <div className="modal-header">
        <h2>🔐 최종 빌링키 등록</h2>
        <p>로그인이 완료되었습니다. 이제 카드를 안전하게 등록하겠습니다</p>
        <button className="close-button" onClick={onClose}>×</button>
      </div>
      
      {/* 진행률 표시 */}
      <div className="progress-indicator">
        <div className="step completed">✅ 플랜 선택</div>
        <div className="step completed">💳 카드정보 저장</div>
        <div className="step completed">👤 로그인 완료</div>
        <div className="step active">🔐 빌링키 등록</div>
        <div className="step">🎉 완료</div>
      </div>
      
      {/* 사용자 정보 표시 */}
      {savedUserData && (
        <div className="user-info-summary">
          <h4>가입 정보</h4>
          {savedUserData.name && <p>👤 {savedUserData.name}</p>}
          <p>📧 {savedUserData.email}</p>
        </div>
      )}
      
      <div className="certification-section">
        <div className="certification-info">
          <div className="info-card">
            <h4>최종 빌링키 등록</h4>
            <ul>
              <li>✅ 카드 정보가 임시로 저장되었습니다</li>
              <li>✅ 로그인이 완료되었습니다</li>
              <li>이제 카드를 안전하게 등록하여 빌링키를 발급받습니다</li>
              <li>등록 완료 후 이용권 페이지로 이동하여 결제하세요</li>
            </ul>
          </div>
        </div>
        
        <button
          className="certification-btn"
          onClick={handlePostBillingKeySignup}
          disabled={isLoading}
        >
          {isLoading ? (
            <>처리 중...</>
          ) : (
            <>
              <span className="icon">🔐</span>
              카드 등록 및 빌링키 발급하기
            </>
          )}
        </button>
      </div>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      <div className="modal-footer">
        <p>빌링키 등록 완료 후 이용권 페이지에서 최종 결제를 진행합니다.</p>
        <p className="small-text">카드 정보는 안전하게 암호화되어 저장됩니다.</p>
      </div>
    </div>
  );

  return (
    <div className="express-checkout-overlay">
      <div className="express-checkout-modal">
        {currentStep === 'payment' && renderPaymentStep()}
        {currentStep === 'auth' && renderAuthStep()}
        {currentStep === 'phone' && renderPhoneStep()}
        {currentStep === 'success' && renderSuccessStep()}
      </div>
    </div>
  );
};

export default ExpressCheckoutModal;